import isEqual from 'lodash/isEqual';

import type { CustomRoleTaxonomySelection } from '@revelio/data-access';

import { activityDefinition } from '../definitions/activity';
import { activityTaxonomyDefinition } from '../definitions/activityTaxonomy';
import {
  decideNumericTaxonomy,
  decideRoleTaxonomy,
  readConcreteTaxonomyPreferenceId,
  readExplicitRoleTaxonomyPreference,
} from '../hooks/adaptiveTaxonomyDecision';
import {
  DEFAULT_ACTIVITY_TAXONOMY_ID,
  getActivityTaxonomyValue,
} from '../hooks/useAdaptiveActivityTaxonomy';
import { getRoleTaxonomyValue } from '../hooks/useAdaptiveRoleTaxonomy';
import {
  DEFAULT_SKILL_TAXONOMY_ID,
  getSkillTaxonomyValue,
} from '../hooks/useAdaptiveSkillTaxonomy';
import {
  type Filter,
  FilterId,
  FilterType,
  isMultiFilter,
  isSingleValueFilter,
} from '../types/filters';
import type { FilterConsistencyState } from './filterGroupConsistency';

/**
 * The async data the adaptive taxonomy derivation depends on. Injected into the
 * reducer (via a ref) so the derivation is a pure function of state + this
 * context, but can still see data that loads after mount.
 */
export interface AdaptiveTaxonomyContext {
  /** RICS k400 tree; empty until it loads. */
  ricsK400List: Parameters<typeof getRoleTaxonomyValue>[1];
  /** Whether the current skill/activity taxonomy versions are still loading. */
  loadingTaxonomyVersions: boolean;
  /** Validates a computed skill taxonomy id against the loaded versions. */
  isValidSkillTaxonomyId: (taxonomyId: number) => boolean;
  isValidActivityTaxonomyId: (taxonomyId: number) => boolean;
  /** Live activity filter gate — drives the membership invariant below. */
  isActivityTaxonomyEnabled: boolean;
  /** Current activity taxonomy version, for stamping late-inserted filters. */
  activityTaxonomyVersion?: string;
}

/**
 * Membership invariant for the flag-gated activity filters: they exist in the
 * ACTIVE group map iff the activity filter is on. Enforced inside the
 * reducer so every writer converges on the same state — `createDefaultFilterMap`
 * gates only the maps it creates, and the flag's real value can arrive AFTER
 * the initial map is built (`useUnleashReady` reports ready on
 * `flagsReady || flagsError`, which can precede this flag's value by a render
 * or a whole retry). Also strips re-insertions from flag-blind writers.
 */
export const enforceActivityFilterMembership = (
  state: FilterConsistencyState,
  ctx: AdaptiveTaxonomyContext
): FilterConsistencyState => {
  if (!state.activeKey) return state;
  const map = state.groupMap.get(state.activeKey);
  if (!map) return state;

  const hasTree = map.has(FilterId.ACTIVITY);
  const hasMultiTree = map.has(FilterId.MULTI_ACTIVITY);
  const hasTaxonomy = map.has(FilterId.ACTIVITY_TAXONOMY);

  if (ctx.isActivityTaxonomyEnabled) {
    if (hasTree && hasTaxonomy) return state;
    const nextMap = new Map(map);
    if (!hasTree) {
      nextMap.set(FilterId.ACTIVITY, { ...activityDefinition });
    }
    if (!hasTaxonomy) {
      nextMap.set(FilterId.ACTIVITY_TAXONOMY, {
        ...activityTaxonomyDefinition,
        value: {
          ...activityTaxonomyDefinition.value,
          version: ctx.activityTaxonomyVersion,
        },
      });
    }
    const nextGroupMap = new Map(state.groupMap);
    nextGroupMap.set(state.activeKey, nextMap);
    return { ...state, groupMap: nextGroupMap };
  }

  if (!hasTree && !hasMultiTree && !hasTaxonomy) return state;
  const nextMap = new Map(map);
  nextMap.delete(FilterId.ACTIVITY);
  nextMap.delete(FilterId.MULTI_ACTIVITY);
  nextMap.delete(FilterId.ACTIVITY_TAXONOMY);
  const nextGroupMap = new Map(state.groupMap);
  nextGroupMap.set(state.activeKey, nextMap);
  return { ...state, groupMap: nextGroupMap };
};

const treeHasSelections = (filter: Filter | undefined): boolean => {
  if (filter === undefined) return false;
  if (isMultiFilter(filter)) return filter.instances.some(treeHasSelections);
  return filter.type === FilterType.TREE && filter.selections.length > 0;
};

/**
 * Whether any of these tree filters holds selections: the plain filter, or
 * the multi filter it becomes on a page that filters with expressions.
 */
const hasTreeSelections = (
  map: ReadonlyMap<FilterId, Filter>,
  treeFilterIds: readonly FilterId[]
): boolean => treeFilterIds.some((id) => treeHasSelections(map.get(id)));

const isLinkedSpecificTaxonomy = (filter: Filter): boolean =>
  filter.isLinkApplied === true && filter.isUserSubmitted === true;

type NumericTaxonomySelection = { taxonomyId?: number; version?: string };

/**
 * The numeric-taxonomy derivations (skill, activity) share one shape; role
 * stays bespoke below because its selection is an object union. Adding a
 * taxonomy here is the whole cost of joining the adaptive derivation.
 */
const NUMERIC_TAXONOMY_DERIVATIONS: ReadonlyArray<{
  taxonomyFilterId: FilterId;
  /** The tree filters whose selections freeze the derivation. */
  treeFilterIds: readonly FilterId[];
  prefKey: 'skill' | 'activity';
  defaultTaxonomyId: number;
  getValue: (
    companyFilter: Parameters<typeof getSkillTaxonomyValue>[0],
    ricsK400List: Parameters<typeof getSkillTaxonomyValue>[1]
  ) => NumericTaxonomySelection;
  isValidId: (ctx: AdaptiveTaxonomyContext) => (taxonomyId: number) => boolean;
}> = [
  {
    taxonomyFilterId: FilterId.SKILL_TAXONOMY,
    treeFilterIds: [FilterId.CUSTOM_SKILL, FilterId.MULTI_CUSTOM_SKILL],
    prefKey: 'skill',
    defaultTaxonomyId: DEFAULT_SKILL_TAXONOMY_ID,
    getValue: getSkillTaxonomyValue,
    isValidId: (ctx) => ctx.isValidSkillTaxonomyId,
  },
  {
    taxonomyFilterId: FilterId.ACTIVITY_TAXONOMY,
    treeFilterIds: [FilterId.ACTIVITY, FilterId.MULTI_ACTIVITY],
    prefKey: 'activity',
    defaultTaxonomyId: DEFAULT_ACTIVITY_TAXONOMY_ID,
    getValue: getActivityTaxonomyValue,
    isValidId: (ctx) => ctx.isValidActivityTaxonomyId,
  },
];

const withTaxonomyReady = (
  state: FilterConsistencyState,
  ready: boolean
): FilterConsistencyState =>
  state.taxonomyReady === ready ? state : { ...state, taxonomyReady: ready };

/**
 * Derive the adaptive role + skill taxonomy for the active COMPANY group and
 * fold it into `state` — the SINGLE place the adaptive taxonomy is computed.
 *
 * Runs inside the reducer after every action (see `FilterProvider`), so the
 * taxonomy is always consistent with the company selection in the same
 * transition that changed it — no post-render recompute, no query flip. Because
 * every entry point (manual picker, AI apply's REPLACE_FILTERS, deeplink,
 * comparison) reduces through here, they all get the same, once-settled value.
 *
 * `taxonomyReady` is written atomically with the value: it is `false` only when
 * a company is selected but the async deps (RICS tree / taxonomy versions)
 * haven't loaded, so the composition query stays paused until a `REDERIVE`
 * re-runs this once they do — with no window where the flag is true but the
 * value is stale.
 */
export const deriveAdaptiveTaxonomies = (
  state: FilterConsistencyState,
  ctx: AdaptiveTaxonomyContext
): FilterConsistencyState => {
  // Adaptive taxonomy is a COMPANY-primary concept; elsewhere there is nothing
  // to wait on, so the taxonomy is always "ready".
  if (state.activeKey !== FilterId.COMPANY) {
    return withTaxonomyReady(state, true);
  }

  const map = state.groupMap.get(state.activeKey);
  if (!map) {
    return withTaxonomyReady(state, true);
  }

  // The industry context that drives the adaptive taxonomy is spread across two
  // filters: the Company tree (whose company ids encode their k400 ancestry, and
  // whose primary form still takes industries) and the Industry filter. Derive
  // from the union so an industry picked in either place adapts the taxonomy.
  const companyFilter = (() => {
    const company = map.get(FilterId.COMPANY);
    const industry = map.get(FilterId.INDUSTRY);
    const selections = [
      ...(company?.type === FilterType.TREE ? company.selections : []),
      ...(industry?.type === FilterType.TREE ? industry.selections : []),
    ];
    if (company?.type === FilterType.TREE) {
      return { ...company, selections };
    }
    return industry?.type === FilterType.TREE
      ? { ...industry, selections }
      : undefined;
  })();
  const hasCompany = (companyFilter?.selections.length ?? 0) > 0;

  // No company or industry → nothing adaptive to resolve; ready. Deps not loaded
  // → can't finalize yet; not ready (a REDERIVE fires when they load).
  const depsReady = ctx.ricsK400List.length > 0 && !ctx.loadingTaxonomyVersions;
  if (!hasCompany || !companyFilter) {
    return withTaxonomyReady(state, true);
  }
  if (!depsReady) {
    return withTaxonomyReady(state, false);
  }

  let nextMap = map;

  const roleTaxonomy = map.get(FilterId.ROLE_TAXONOMY);
  if (
    roleTaxonomy &&
    isSingleValueFilter<CustomRoleTaxonomySelection>(roleTaxonomy)
  ) {
    const decision = decideRoleTaxonomy({
      currentValue: roleTaxonomy.value,
      currentIsUserSubmitted: roleTaxonomy.isUserSubmitted,
      isPinned:
        roleTaxonomy.isAiApplied === true ||
        isLinkedSpecificTaxonomy(roleTaxonomy),
      explicitPreference: roleTaxonomy.isLinkApplied
        ? undefined
        : readExplicitRoleTaxonomyPreference(),
      freezeOnSelections: hasTreeSelections(map, [
        FilterId.ROLE,
        FilterId.MULTI_ROLE,
      ]),
      computeAdaptiveValue: () =>
        getRoleTaxonomyValue(companyFilter, ctx.ricsK400List),
    });
    if (
      !isEqual(decision.value, roleTaxonomy.value) ||
      (roleTaxonomy.isUserSubmitted ?? false) !== decision.isUserSubmitted
    ) {
      if (nextMap === map) nextMap = new Map(map);
      nextMap.set(FilterId.ROLE_TAXONOMY, {
        ...roleTaxonomy,
        value: decision.value,
        isUserSubmitted: decision.isUserSubmitted,
      });
    }
  }

  for (const derivation of NUMERIC_TAXONOMY_DERIVATIONS) {
    const taxonomyFilter = map.get(derivation.taxonomyFilterId);
    if (
      !taxonomyFilter ||
      !isSingleValueFilter<NumericTaxonomySelection>(taxonomyFilter)
    ) {
      continue;
    }
    const decision = decideNumericTaxonomy({
      currentValue: taxonomyFilter.value,
      currentIsUserSubmitted: taxonomyFilter.isUserSubmitted,
      isPinned:
        taxonomyFilter.isAiApplied === true ||
        isLinkedSpecificTaxonomy(taxonomyFilter),
      concretePreferenceId: taxonomyFilter.isLinkApplied
        ? undefined
        : readConcreteTaxonomyPreferenceId(derivation.prefKey),
      freezeOnSelections: hasTreeSelections(map, derivation.treeFilterIds),
      computeAdaptiveValue: () => {
        const value = derivation.getValue(companyFilter, ctx.ricsK400List);
        return derivation.isValidId(ctx)(
          value.taxonomyId ?? derivation.defaultTaxonomyId
        )
          ? value
          : { taxonomyId: derivation.defaultTaxonomyId };
      },
    });
    if (
      !isEqual(decision.value, taxonomyFilter.value) ||
      (taxonomyFilter.isUserSubmitted ?? false) !== decision.isUserSubmitted
    ) {
      if (nextMap === map) nextMap = new Map(map);
      nextMap.set(derivation.taxonomyFilterId, {
        ...taxonomyFilter,
        value: decision.value,
        isUserSubmitted: decision.isUserSubmitted,
      });
    }
  }

  if (nextMap === map) {
    return withTaxonomyReady(state, true);
  }
  const nextGroupMap = new Map(state.groupMap);
  nextGroupMap.set(state.activeKey, nextMap);
  return { ...state, groupMap: nextGroupMap, taxonomyReady: true };
};
